import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { FSAccountOption, MEOption, VarianceTypeOption } from '../cbc-reconciliation/cbc-reconciliation.service';
import { CbcReconciliationService } from '../cbc-reconciliation/cbc-reconciliation.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import {
  findAppkitItemByValue,
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../../shared/utils/appkit-dropdown.util';

@Component({
    selector: 'gtw-cbc-create-rec-data',
    templateUrl: './cbc-create-rec-data.component.html',
    styleUrls: ['./cbc-create-rec-data.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CbcCreateRecDataComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  modalTitle = 'Reconciling Item - Add/Edit Entry';
  minimise = false;
  saveLoading = false;

  ME: MEOption[] = [];
  meOptions: { label: string; value: string }[] = [];
  selectedME: string | null = null;
  fsAccounts: FSAccountOption[] = [];
  fsAccOptions: { label: string; value: string }[] = [];
  selectedFSAcc: string | null = null;
  varianceTypes: VarianceTypeOption[] = [];
  varianceTypeValues: { label: string; value: string; name?: string }[] = [];
  selectedVarianceType: string | null = null;
  selectedMEItem: any = null;
  selectedFSAccItem: any = null;
  selectedVarianceTypeItem: any = null;
  meSelected = false;
  fsAcc = false;
  typeSel = false;
  comments = '';
  amount1: number | null = 0;

  filterParams: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  globalService: any = {};
  private isInitialized = false;
  isLoading = false;

  constructor(
    private cbcService: CbcReconciliationService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.markForCheck();
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.GENERAL_CONFIG?.base_url || !this.filterParams || Object.keys(this.filterParams).length === 0
    || !this.globalService || Object.keys(this.globalService).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    this.isLoading = true;
    this.cdr.detectChanges();
    this.cbcService.getMEData(this.baseUrl, this.filterParams, this.ssoId, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        if (response && response.length > 0) {
          this.ME = response;
          this.meOptions = response.map((m) => ({ label: m.ME_NAME || m.ME_CODE, value: m.ME_CODE }));
          const parentMe = this.filterParams?.parent_me_string;
          const meKeyArray = parentMe ? String(parentMe).split(',') : [];
          if (meKeyArray.length > 0 && meKeyArray.length !== this.ME.length) {
            for (const element of this.ME) {
              for (let i = 0; i < meKeyArray.length; i++) {
                if (String(element.ME_KEY) === meKeyArray[i]) {
                  this.selectedME = element.ME_CODE;
                  break;
                }
              }
              if (this.selectedME) break;
            }
          } else if (meKeyArray.length > 0) {
            const meKey = meKeyArray.length > 1 ? meKeyArray[1] : meKeyArray[0];
            const findME = this.ME.find((elem) => String(elem.ME_KEY) === meKey);
            if (findME) this.selectedME = findME.ME_CODE;
          }
          if (!this.selectedME && this.ME.length) {
            this.selectedME = this.ME[0].ME_CODE;
          }
          this.meSelected = true;
          this.loadVarianceTypes();
        } else {
          this.showForm();
        }
      },
      error: (err) => {
        this.gtwSnackbarService.openSnackBar(err?.message || 'Failed to load ME data', 'gtw-snackbar--red');
        this.showForm();
      },
    });
  }

  loadVarianceTypes(): void {
    this.cbcService.getVarianceType(this.baseUrl, this.filterParams, this.ssoId, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.varianceTypeValues = mapToAppkitDropdownItems(
          (response || []).map((v) => ({ name: v.VARIANCE_TYPE, value: v.VARIANCE_TYPE })),
          'name',
          'value'
        );
        this.selectedVarianceType =
          this.varianceTypeValues.length > 0 ? this.varianceTypeValues[0].value : null;
        this.typeSel = true;
        this.onVarianceTypeSelect();
        this.cbcService.reconcFSAccounts(this.baseUrl, this.filterParams, this.ssoId, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (accResp) => {
            this.fsAccounts = accResp;
            this.fsAccOptions = accResp.map((a) => ({ label: a.ACCT_DESC || a.ACCT, value: a.ACCT }));
            this.selectedFSAcc = accResp.length > 0 ? accResp[0].ACCT : null;
            this.fsAcc = true;
            this.onFSAccountSelect();
            this.syncDropdownSelections();
            this.showForm();
          },
          error: (err) => {
            this.gtwSnackbarService.openSnackBar(err?.message || 'Failed to load FS accounts', 'gtw-snackbar--red');
            this.showForm();
          },
        });
      },
      error: (err) => {
        this.gtwSnackbarService.openSnackBar(err?.message || 'Failed to load variance types', 'gtw-snackbar--red');
        this.showForm();
      },
    });
  }

  private showForm(): void {
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  onMEChange(value: string | null): void {
    this.selectedME = value;
    this.onMESelect();
  }

  onFSAccChange(value: string | null): void {
    this.selectedFSAcc = value;
    this.onFSAccountSelect();
  }

  onVarianceTypeChange(value: string | null): void {
    this.selectedVarianceType = value;
    this.typeSel = value != null;
    if (this.typeSel && this.fsAcc && this.selectedME && this.selectedFSAcc && this.selectedVarianceType) {
      this.fetchEntryData();
    }
    this.syncDropdownSelections();
    this.cdr.detectChanges();
  }

  onMEDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedMEItem = selected;
    this.onMEChange(selected?.value ?? null);
  }

  onFSAccDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedFSAccItem = selected;
    this.onFSAccChange(selected?.value ?? null);
  }

  onVarianceTypeDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedVarianceTypeItem = selected;
    this.onVarianceTypeChange(selected?.value ?? null);
  }

  onMESelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) this.selectedMEItem = selected;
  }

  onFSAccSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) this.selectedFSAccItem = selected;
  }

  onVarianceTypeSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) this.selectedVarianceTypeItem = selected;
  }

  private syncDropdownSelections(): void {
    this.selectedMEItem = findAppkitItemByValue(this.meOptions, this.selectedME);
    this.selectedFSAccItem = findAppkitItemByValue(this.fsAccOptions, this.selectedFSAcc);
    this.selectedVarianceTypeItem = findAppkitItemByValue(this.varianceTypeValues, this.selectedVarianceType);
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  onCommentsChange(value: string): void {
    this.comments = value;
    this.cdr.detectChanges();
  }

  onMESelect(): void {
    this.meSelected = true;
    if (this.typeSel && this.fsAcc && this.selectedME && this.selectedFSAcc && this.selectedVarianceType) {
      this.fetchEntryData();
    }
    this.cdr.detectChanges();
  }

  onFSAccountSelect(): void {
    this.fsAcc = true;
    if (this.typeSel && this.selectedME && this.selectedFSAcc && this.selectedVarianceType) {
      this.fetchEntryData();
    }
    this.cdr.detectChanges();
  }

  onVarianceTypeSelect(): void {
    this.typeSel = true;
    if (this.fsAcc && this.selectedME && this.selectedFSAcc && this.selectedVarianceType) {
      this.fetchEntryData();
    }
    this.cdr.detectChanges();
  }

  private fetchEntryData(): void {
    if (!this.selectedME || !this.selectedFSAcc || !this.selectedVarianceType) return;
    this.cbcService
      .getEntryData(
        this.baseUrl,
        this.filterParams,
        this.ssoId,
        this.selectedME,
        this.selectedFSAcc,
        this.selectedVarianceType,
        this.globalService
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response && response.length > 0) {
            this.comments = response[0].COMMENTS ?? '';
            this.amount1 = response[0].AMOUNT_1207 ?? 0;
          } else {
            this.comments = '';
            this.amount1 = 0;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.comments = '';
          this.amount1 = 0;
          this.cdr.detectChanges();
        },
      });
  }

  parseAmount(v: number | string | null): number {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    const s = String(v).replace(/[^\d.\-]/g, '');
    const n = parseFloat(s);
    return Number.isNaN(n) ? 0 : n;
  }

  save(): void {
    if (this.saveLoading || !this.selectedME || !this.selectedFSAcc || !this.selectedVarianceType) return;
    const clobData = [
      {
        ME_CODE: this.selectedME,
        ACCT: this.selectedFSAcc,
        AMOUNT_1204: null,
        AMOUNT_1205: null,
        AMOUNT_1206: null,
        AMOUNT_1207: this.parseAmount(this.amount1),
        COMMENTS: this.comments,
        VARIANCE_TYPE: this.selectedVarianceType,
        POPUP_KEY: 2,
      },
    ];
    this.saveLoading = true;
    this.cdr.detectChanges();
    this.cbcService.saveData(this.baseUrl, clobData, this.filterParams, this.ssoId, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.saveLoading = false;
        if (response?.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Data is successfully updated.', 'gtw-snackbar--green');
          this.cancel();
          setTimeout(() => {
            const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
            if (activeScreen?.screen_key) {
              this.gtwWorkspace.refreshGridData.next(activeScreen);
            }
          }, 500);
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.saveLoading = false;
        this.gtwSnackbarService.openSnackBar(err?.message || 'Error saving.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true,
    });
    document
      .querySelector('gtw-cbc-create-rec-data-ce')
      ?.dispatchEvent(updateEvent);
  }
}
